import React from "react";
import { Recording, ContractType, PayableTo } from "@quantizr-engineering/common/types/recording";
import {
  FaFileAlt,
  FaInfoCircle,
  FaMusic,
  FaUsers,
  FaBalanceScale,
} from "react-icons/fa";
import { prettyDate } from "../../utils/dateFormatters";

interface RecordingDetailsProps {
  recording: Recording;
}

const RecordingDetails: React.FC<RecordingDetailsProps> = ({ recording }) => {
  return (
    <div className="card bg-white rounded-lg p-4 my-2 shadow-md w-full">
      <div className="flex flex-row items-center">
        <FaMusic className="mr-1 text-blue-800" />
        <h2 className="text-xl font-bold">
          {recording.title || "Untitled Recording"}
        </h2>
      </div>

      <div className="flex flex-row items-center w-full mt-2">
        <FaInfoCircle className="mr-1 text-blue-800" />
        {recording.artistName ? (
          <div className="flex items-center">{recording.artistName}</div>
        ) : (
          <span className="inline-block bg-gray-200 text-gray-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded">
            No Stage Name available
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4 mt-4">
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Release Date
          </label>
          <span className="inline-block bg-green-200 text-green-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded">
            {recording.releaseDate
              ? `${prettyDate(new Date(recording.releaseDate)).month} ${
                  prettyDate(new Date(recording.releaseDate)).day
                }`
              : "Not set"}
          </span>
        </div>
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Contract Date
          </label>
          <span className="inline-block bg-purple-200 text-purple-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded">
            {recording.contractDate
              ? `${prettyDate(new Date(recording.contractDate)).month} ${
                  prettyDate(new Date(recording.contractDate)).day
                }`
              : "Not set"}
          </span>
        </div>
      </div>

      {recording.songContracts && recording.songContracts.length > 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Song Contracts</h3>
          {recording.songContracts.map((contract, index) => (
            <div key={index} className="bg-gray-100 p-2 rounded mb-2">
              <p>
                <strong>Name:</strong> {contract.contractName}
              </p>
              <p>
                <strong>Type:</strong> {contract.contractType.join(", ")}
              </p>
              <p>
                <strong>Date:</strong>
                {`${prettyDate(new Date(contract.contractDate)).month} ${
                  prettyDate(new Date(contract.contractDate)).day
                }`}
              </p>
            </div>
          ))}
        </div>
      )}

      {recording.collaborators && recording.collaborators.length > 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Collaborators</h3>
          {recording.collaborators.map((collaborator, index) => (
            <div key={index} className="bg-gray-100 p-2 rounded mb-2">
              <p>
                <strong>Name:</strong> {collaborator.name}
              </p>
              <p>
                <strong>Roles:</strong>{" "}
                {[
                  collaborator.producer && "Producer",
                  collaborator.musician && "Musician",
                  collaborator.vocalist && "Vocalist",
                  collaborator.writer && "Writer",
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            </div>
          ))}
        </div>
      )}

      {recording.songRights && recording.songRights.length > 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Song Rights</h3>
          {recording.songRights.map((rights, index) => (
            <div key={index} className="bg-gray-100 p-2 rounded mb-2">
              <p>
                <strong>Record Label:</strong>{" "}
                {rights.recordLabel ? "Yes" : "No"}
              </p>
              <p>
                <strong>Name:</strong> {rights.name}
              </p>
              <p>
                <strong>Term:</strong> {rights.term} years
              </p>
              <p>
                <strong>Territory:</strong> {rights.territory}
              </p>
              <p>
                <strong>Exclusive:</strong> {rights.exclusive ? "Yes" : "No"}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecordingDetails;
